import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { checkAndSetToken, handleError } from './interceptor';
import { BACK_URL, TIMEOUT } from 'apis';
import { getNewRefreshToken } from './auth/getNewRefreshToken';
import { setTokensInCookies } from '../lib/utils/setTokensInCookies';

/** Axios 인스턴스의 설정과 요청 및 응답 인터셉터를 관리하는 파일 **/

interface FailedRequest {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
}

export const axiosInstance = axios.create({
  // `url`이 절대값이 아닌 경우 `baseURL`은 URL 앞에 붙습니다.
  // 상대적인 URL을 인스턴스 메서드에 전달하려면 `baseURL`을 설정하는 것은 편리합니다.
  baseURL: BACK_URL,
  // `timeout`은 요청이 시간 초과되기 전의 시간(밀리초)을 지정합니다.
  // 요청이 `timeout`보다 오래 걸리면 요청이 중단됩니다.
  timeout: TIMEOUT, // 기본값은 `0` (타임아웃 없음)

  // `withCredentials`은 자격 증명을 사용하여 사이트 간 액세스 제어 요청을 해야 하는지 여부를 나타냅니다.
  withCredentials: true, // 기본값: false
  headers: {
    // Accept: '*/*',
    'Content-Type': 'application/json',
    'Accept-Language': localStorage.getItem('language') || 'ko'
  }
  // `auth`는 HTTP Basic 인증이 사용되며, 자격 증명을 제공합니다.
  // `auth`를 사용하면, `Authorization` 헤더가 설정되어 `headers`를 사용하여 설정한 기존의 `Authorization` 사용자 지정 헤더를 덮어씁니다.
  // 이 파라미터를 통해 HTTP Basic 인증만 구성할 수 있음을 참고하세요.
  // Bearer 토큰 등의 경우 `Authorization` 사용자 지정 헤더를 대신 사용합니다.
  // auth: {
  //   username: 'janedoe',
  //   password: 's00pers3cret',
  // },
});

// 요청 인터셉터
axiosInstance.interceptors.request.use(checkAndSetToken, handleError);
// 응답 인터셉터
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: any) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // /api/login 요청에 대한 예외 처리
    if (originalRequest.url === '/login') {
      return Promise.reject(error); // /api/login은 다시 시도하지 않음
    }

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // 이미 토큰 갱신 중인 경우 새로운 프로미스를 생성하여 대기열에 추가
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        getNewRefreshToken().then((newToken) => {
          const accessToken = newToken.accessToken;
          // 새로운 토큰을 쿠키에 저장
          setTokensInCookies(newToken);

          axios.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
          processQueue(null, accessToken);
          resolve(axios(originalRequest));
        }).catch((err) => {
          processQueue(err, null);
          reject(err);
        }).finally(() => {
          isRefreshing = false;
        });
      });
    }

    return handleError(error);
  }
);
